import React, { ChangeEvent, EventHandler, FC, RefObject } from 'react';
import classNames from 'classnames';
import classes from './styles/index.module.scss';
import { codes } from '../../data/translations';
import { useTranslationAPI } from '../../modules/translations';
import { isUndefined } from '../../predicates/type';
import { HELLOFLEX_TEXTAREA_CLASS } from './const';

export type TextareaProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size' | 'onChange' | 'value'> & {
	value?: string;
	invalid?: boolean;
	noResize?: boolean;
	verticalResize?: boolean;
	horizontalResize?: boolean;
	className?: string;
	onChange?: (value: string) => void;
	rows?: number;
	cols?: number;
	setRef?: RefObject<HTMLTextAreaElement>;
	showCounter?: boolean;
	counterLabel?: string;
	maxLength?: number;
	name?: string;
};

export const Textarea: FC<React.PropsWithChildren<TextareaProps>> = (props) => {
	const { translate } = useTranslationAPI();
	const handleInputChange: EventHandler<ChangeEvent<HTMLTextAreaElement>> = (e) => {
		const { value } = e.target;

		if (props.disabled || isUndefined(props.onChange)) {
			return;
		}

		props.onChange(value);
	};

	const {
		value,
		disabled,
		invalid,
		onFocus,
		onBlur,
		rows,
		cols,
		placeholder,
		className,
		setRef,
		verticalResize,
		horizontalResize,
		name,
		maxLength,
		showCounter,
		noResize,
		counterLabel,
		style,
	} = props;

	const componentClasses = classNames(classes.textarea, HELLOFLEX_TEXTAREA_CLASS, className, {
		[classes.textarea_invalid]: invalid,
		[classes.textarea_disabled]: disabled,
		[classes.textarea_noResizable]: noResize,
		[classes.textarea_verticallyResizable]: verticalResize,
		[classes.textarea_horizontallyResizable]: horizontalResize,
	});

	const defaultCounterLabel = translate(codes.common.charactersAvailable);

	return (
		<>
			<textarea
				onBlur={onBlur}
				onChange={handleInputChange}
				onFocus={onFocus}
				className={componentClasses}
				style={style}
				cols={cols}
				rows={rows}
				disabled={disabled}
				readOnly={disabled}
				placeholder={placeholder || translate(codes.common.fillIn)}
				value={value}
				ref={setRef}
				name={name}
			/>
			{showCounter && maxLength && (
				<div className={classes.textareaCounter}>
					{`${counterLabel || defaultCounterLabel} ${maxLength - (value ? value.length : 0)}`}
				</div>
			)}
		</>
	);
};

Textarea.defaultProps = {
	value: '',
	placeholder: '',
	disabled: false,
	invalid: false,
	noResize: false,
	verticalResize: false,
	horizontalResize: false,
	className: '',
	showCounter: false,
	counterLabel: '',
	onChange: () => {},
	onFocus: () => {},
	onBlur: () => {},
};
